import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  api, formatApiError, formatClockTime, formatDate, formatDuration, todayISODate,
} from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { HandoffHistory } from "@/components/HandoffHistory";
import {
  RefreshCw, Timer, User as UserIcon, Package, Calendar, ChevronDown, ChevronRight, Hourglass,
} from "lucide-react";

export default function Reports() {
  const [startDate, setStartDate] = useState(todayISODate());
  const [endDate, setEndDate] = useState(todayISODate());
  const [filterId, setFilterId] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReports = async (s = startDate, e = endDate) => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports", { params: { start_date: s, end_date: e } });
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReports(); /* eslint-disable-next-line */ }, []);

  const setQuickRange = (s, e) => {
    setStartDate(s);
    setEndDate(e);
    fetchReports(s, e);
  };

  const handleStart = (e) => {
    const v = e.target.value;
    setStartDate(v);
    if (v > endDate) {
      setEndDate(v);
      fetchReports(v, v);
    } else {
      fetchReports(v, endDate);
    }
  };

  const handleEnd = (e) => {
    const v = e.target.value;
    setEndDate(v);
    fetchReports(startDate, v);
  };

  const visibleCouriers = useMemo(() => {
    if (!data) return [];
    return filterId === "all" ? data.couriers : data.couriers.filter((c) => c.courier_id === filterId);
  }, [data, filterId]);

  const totals = useMemo(() => {
    const list = visibleCouriers;
    const total = list.reduce((s, c) => s + c.delivered_count, 0);
    const allDur = list.flatMap((c) => c.orders.map((o) => o.duration_seconds).filter((x) => x != null));
    const allPickup = list.flatMap((c) => c.orders.map((o) => o.pickup_delay_seconds).filter((x) => x != null));
    const avgDur = allDur.length ? Math.round(allDur.reduce((s, n) => s + n, 0) / allDur.length) : 0;
    const avgPickup = allPickup.length ? Math.round(allPickup.reduce((s, n) => s + n, 0) / allPickup.length) : 0;
    return { total, avgDur, avgPickup };
  }, [visibleCouriers]);

  return (
    <div className="min-h-screen bg-zinc-50">
      <AppHeader
        title="Raporlar"
        subtitle="Kurye Performansı"
        right={
          <button
            data-testid="refresh-button"
            onClick={() => fetchReports()}
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 border-2 border-zinc-200 hover:border-zinc-950 transition-colors text-xs font-bold uppercase tracking-wider"
          >
            <RefreshCw size={14} strokeWidth={2.5} /> Yenile
          </button>
        }
      />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Filter bar */}
        <div className="bg-white border-2 border-zinc-200 brutal-shadow p-4 sm:p-5 mb-6 grid grid-cols-1 sm:grid-cols-12 gap-3 sm:items-end">
          <div className="sm:col-span-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1.5">
              Başlangıç
            </div>
            <div className="flex items-center bg-zinc-50 border-2 border-zinc-200 focus-within:border-zinc-950 px-3">
              <Calendar size={16} strokeWidth={2.5} className="text-zinc-500" />
              <input
                data-testid="report-start-date"
                type="date"
                value={startDate}
                onChange={handleStart}
                className="flex-1 bg-transparent px-2 py-2.5 text-base outline-none"
              />
            </div>
          </div>

          <div className="sm:col-span-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1.5">
              Bitiş
            </div>
            <div className="flex items-center bg-zinc-50 border-2 border-zinc-200 focus-within:border-zinc-950 px-3">
              <Calendar size={16} strokeWidth={2.5} className="text-zinc-500" />
              <input
                data-testid="report-end-date"
                type="date"
                value={endDate}
                min={startDate}
                onChange={handleEnd}
                className="flex-1 bg-transparent px-2 py-2.5 text-base outline-none"
              />
            </div>
          </div>

          <div className="sm:col-span-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1.5">
              Kurye
            </div>
            <select
              data-testid="report-courier-filter"
              value={filterId}
              onChange={(e) => setFilterId(e.target.value)}
              className="w-full bg-zinc-50 border-2 border-zinc-200 focus:border-zinc-950 px-3 py-2.5 text-base outline-none"
            >
              <option value="all">Tüm Kuryeler</option>
              {data?.couriers.map((c) => (
                <option key={c.courier_id} value={c.courier_id}>{c.courier_name}</option>
              ))}
            </select>
          </div>

          <div className="sm:col-span-3 flex flex-wrap gap-2">
            <QuickDate
              label="Bugün"
              onClick={() => {
                const d = todayISODate();
                setQuickRange(d, d);
              }}
              active={startDate === endDate && startDate === todayISODate()}
              testId="qd-today"
            />
            <QuickDate
              label="Dün"
              onClick={() => {
                const d = new Date(); d.setDate(d.getDate() - 1);
                const tz = d.getTimezoneOffset() * 60000;
                const s = new Date(d.getTime() - tz).toISOString().slice(0, 10);
                setQuickRange(s, s);
              }}
              testId="qd-yesterday"
            />
            <QuickDate
              label="Son 7 Gün"
              onClick={() => {
                const e = todayISODate();
                const sd = new Date(); sd.setDate(sd.getDate() - 6);
                const tz = sd.getTimezoneOffset() * 60000;
                const s = new Date(sd.getTime() - tz).toISOString().slice(0, 10);
                setQuickRange(s, e);
              }}
              testId="qd-7days"
            />
            <QuickDate
              label="Son 30 Gün"
              onClick={() => {
                const e = todayISODate();
                const sd = new Date(); sd.setDate(sd.getDate() - 29);
                const tz = sd.getTimezoneOffset() * 60000;
                const s = new Date(sd.getTime() - tz).toISOString().slice(0, 10);
                setQuickRange(s, e);
              }}
              testId="qd-30days"
            />
          </div>
        </div>

        {/* Summary row */}
        {data && (
          <div className="grid grid-cols-3 gap-3 mb-6">
            <SummaryStat testId="summary-total" label="Toplam Teslimat" value={totals.total} icon={<Package strokeWidth={2.5} size={16} />} />
            <SummaryStat testId="summary-avg-pickup" label="Ort. Teslim Alma" value={totals.avgPickup > 0 ? formatDuration(totals.avgPickup) : "—"} icon={<Hourglass strokeWidth={2.5} size={16} />} hint="Açılıştan teslim almaya" />
            <SummaryStat testId="summary-avg-dur" label="Ort. Teslim Süresi" value={totals.avgDur > 0 ? formatDuration(totals.avgDur) : "—"} icon={<Timer strokeWidth={2.5} size={16} />} hint="Teslim alma → teslim etme" />
          </div>
        )}

        {loading || !data ? (
          <Skeleton />
        ) : (
          <div className="space-y-6">
            {visibleCouriers.length === 0 && (
              <div className="border-2 border-dashed border-zinc-200 p-10 text-center font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">
                Bu filtreye uyan kurye yok
              </div>
            )}
            {visibleCouriers.map((c) => (
              <CourierReport key={c.courier_id} courier={c} rangeLabel={rangeLabel(data)} />
            ))}

            <LateOrdersSection orders={data.late_orders || []} rangeLabel={rangeLabel(data)} />
          </div>
        )}
      </main>
    </div>
  );
}

function QuickDate({ label, onClick, active, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`flex-1 px-3 py-2.5 text-xs font-bold uppercase tracking-wider border-2 transition-colors ${
        active ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-950"
      }`}
    >
      {label}
    </button>
  );
}

function CourierReport({ courier: c, rangeLabel }) {
  const [open, setOpen] = useState(true);
  return (
    <section
      data-testid={`courier-report-${c.courier_id}`}
      className="bg-white border-2 border-zinc-200 brutal-shadow"
    >
      <header className="px-5 py-4 border-b-2 border-zinc-200 flex items-center justify-between gap-4 flex-wrap">
        <button
          data-testid={`toggle-${c.courier_id}`}
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-3 min-w-0 text-left"
        >
          {open ? <ChevronDown size={18} className="text-zinc-500" /> : <ChevronRight size={18} className="text-zinc-500" />}
          <div className="w-10 h-10 bg-zinc-950 text-white flex items-center justify-center shrink-0">
            <UserIcon strokeWidth={2.5} size={18} />
          </div>
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Kurye</div>
            <div
              data-testid={`courier-name-${c.courier_id}`}
              className="text-xl font-black tracking-tight text-zinc-950 break-words"
            >
              {c.courier_name}
            </div>
          </div>
        </button>
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          <Stat testId={`stat-count-${c.courier_id}`} icon={<Package strokeWidth={2.5} size={14} />} label="Teslim" value={c.delivered_count} />
          <Stat testId={`stat-pickup-${c.courier_id}`} icon={<Hourglass strokeWidth={2.5} size={14} />} label="Alma" value={c.avg_pickup_delay_seconds > 0 ? formatDuration(c.avg_pickup_delay_seconds) : "—"} />
          <Stat testId={`stat-dur-${c.courier_id}`} icon={<Timer strokeWidth={2.5} size={14} />} label="Süre" value={c.avg_delivery_seconds > 0 ? formatDuration(c.avg_delivery_seconds) : "—"} />
        </div>
      </header>

      {open && (
        <div className="p-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
            {rangeLabel} tarihinde teslim edilen siparişler
          </div>
          {c.orders.length === 0 ? (
            <div className="border-2 border-dashed border-zinc-200 p-6 text-center font-mono text-xs uppercase tracking-[0.3em] text-zinc-400">
              Bu tarihte teslim edilen sipariş yok
            </div>
          ) : (
            <div className="overflow-x-auto border-2 border-zinc-200">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b-2 border-zinc-200">
                  <tr className="text-left">
                    <Th>Müşteri</Th>
                    <Th>İşleyen</Th>
                    <Th>Götürecek Kişi</Th>
                    <Th>Teslim Aldı</Th>
                    <Th>Teslim Etti</Th>
                    <Th>Alma Süresi</Th>
                    <Th>Teslim Süresi</Th>
                  </tr>
                </thead>
                <tbody>
                  {c.orders.map((o) => (
                    <tr
                      key={o.id}
                      data-testid={`report-order-${o.id}`}
                      className="border-b border-zinc-100 last:border-b-0"
                    >
                      <Td>
                        <div className="font-bold text-zinc-950">{o.full_name}</div>
                        <div className="font-mono text-[10px] text-zinc-500">{o.phone}</div>
                        {o.handoff_history && o.handoff_history.length > 0 && (
                          <div className="mt-1.5">
                            <HandoffHistory
                              history={o.handoff_history}
                              testIdPrefix={`report-order-${o.id}`}
                              tone="table"
                            />
                          </div>
                        )}
                      </Td>
                      <Td className="text-sm text-zinc-700">{o.created_by_name || "—"}</Td>
                      <Td className="text-sm text-zinc-700">{o.claimed_by_name || "—"}</Td>
                      <Td className="whitespace-nowrap font-mono text-xs">{formatClockTime(o.picked_up_at)}</Td>
                      <Td className="whitespace-nowrap font-mono text-xs">{formatClockTime(o.delivered_at)}</Td>
                      <Td className="whitespace-nowrap font-bold text-amber-700">{formatDuration(o.pickup_delay_seconds)}</Td>
                      <Td className="whitespace-nowrap font-bold text-emerald-700">{formatDuration(o.duration_seconds)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function Stat({ icon, label, value, testId }) {
  return (
    <div data-testid={testId} className="bg-zinc-50 border-2 border-zinc-200 px-3 py-1.5 min-w-[90px]">
      <div className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
        {icon}
        {label}
      </div>
      <div className="text-lg sm:text-xl font-black tracking-tighter text-zinc-950 mt-0.5">{value}</div>
    </div>
  );
}

function SummaryStat({ icon, label, value, testId, hint }) {
  return (
    <div data-testid={testId} className="bg-white border-2 border-zinc-950 brutal-shadow p-4">
      <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 truncate">
        {icon}
        <span className="truncate">{label}</span>
      </div>
      <div className="text-2xl sm:text-3xl font-black tracking-tighter text-zinc-950 mt-1">{value}</div>
      {hint && <div className="text-[10px] text-zinc-500 mt-0.5 font-mono uppercase tracking-wider">{hint}</div>}
    </div>
  );
}

function Th({ children }) {
  return (
    <th className="px-3 py-2 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">
      {children}
    </th>
  );
}

function LateOrdersSection({ orders, rangeLabel }) {
  const [open, setOpen] = useState(true);
  return (
    <section
      data-testid="late-orders-section"
      className="bg-white border-2 border-red-300 brutal-shadow"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 sm:gap-4 px-4 sm:px-6 py-4 border-b-2 border-red-200 hover:bg-red-50 transition-colors"
      >
        <div className="w-10 h-10 bg-red-100 text-red-700 flex items-center justify-center border-2 border-red-300">
          <Hourglass strokeWidth={2.5} size={18} />
        </div>
        <div className="flex-1 text-left min-w-0">
          <h2 className="text-base sm:text-lg font-black tracking-tight text-zinc-950">
            Geç Teslim Edilen Siparişler
          </h2>
          <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mt-0.5">
            Planlanan saatten 30 dk veya daha geç teslim edilenler — {rangeLabel}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-3xl font-black tracking-tight text-red-700" data-testid="late-orders-count">
            {orders.length}
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Adet</div>
        </div>
        {open ? <ChevronDown size={18} strokeWidth={2.5} className="text-zinc-400 shrink-0" /> : <ChevronRight size={18} strokeWidth={2.5} className="text-zinc-400 shrink-0" />}
      </button>

      {open && (
        orders.length === 0 ? (
          <div className="p-6 text-center font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">
            Bu aralıkta geç teslim yok 🎉
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-red-50 border-b-2 border-red-200">
                <tr className="text-left">
                  <Th>Müşteri</Th>
                  <Th>Adres</Th>
                  <Th>Planlanan</Th>
                  <Th>Gerçek</Th>
                  <Th>Gecikme</Th>
                  <Th>İşleyen</Th>
                  <Th>Götürecek</Th>
                  <Th>Teslim Alan</Th>
                  <Th>Teslim Eden</Th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr
                    key={o.id}
                    data-testid={`late-order-${o.id}`}
                    className="border-b border-red-100 last:border-b-0"
                  >
                    <Td>
                      <div className="font-bold text-zinc-950">{o.full_name}</div>
                      <div className="font-mono text-[10px] text-zinc-500">{o.phone}</div>
                    </Td>
                    <Td className="text-xs text-zinc-700 max-w-[16rem] truncate" title={o.address}>{o.address}</Td>
                    <Td className="whitespace-nowrap font-mono text-xs">
                      <div>{formatDate(o.delivery_time)}</div>
                      <div className="text-zinc-500">{formatClockTime(o.delivery_time)}</div>
                    </Td>
                    <Td className="whitespace-nowrap font-mono text-xs">
                      <div>{formatDate(o.delivered_at)}</div>
                      <div className="text-zinc-500">{formatClockTime(o.delivered_at)}</div>
                    </Td>
                    <Td className="whitespace-nowrap font-black text-red-700">
                      +{formatDuration(o.delay_seconds)}
                    </Td>
                    <Td className="text-sm text-zinc-700 whitespace-nowrap">{o.created_by_name || "—"}</Td>
                    <Td className="text-sm text-zinc-700 whitespace-nowrap">{o.claimed_by_name || "—"}</Td>
                    <Td className="text-sm text-zinc-700 whitespace-nowrap">{o.initial_picked_up_by_name || o.picked_up_by_name || "—"}</Td>
                    <Td className="whitespace-nowrap">
                      <div className="text-sm text-zinc-700">{o.picked_up_by_name || "—"}</div>
                      {o.handoff_history && o.handoff_history.length > 0 && (
                        <div className="mt-1">
                          <HandoffHistory
                            history={o.handoff_history}
                            testIdPrefix={`late-order-${o.id}`}
                            tone="table"
                          />
                        </div>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </section>
  );
}

function rangeLabel(d) {
  if (!d) return "—";
  if (d.start_date === d.end_date) return formatDate(d.start_date);
  return `${formatDate(d.start_date)} → ${formatDate(d.end_date)}`;
}

function Td({ children, className = "" }) {
  return <td className={`px-3 py-2.5 align-top ${className}`}>{children}</td>;
}

function Skeleton() {
  return (
    <div className="space-y-4">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="h-40 bg-zinc-100 animate-pulse border-2 border-zinc-200" />
      ))}
    </div>
  );
}
